import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, ShoppingCart, Link as LinkIcon, User, LogOut, LayoutDashboard } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import { useBusinessInfo, telHref } from '../hooks/useBusinessInfo';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import CartDrawer from './CartDrawer';
import { useAuthDrawer } from '../context/AuthDrawerContext';
import TransferSiteHeader from '../tools/transfer/TransferSiteHeader';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [megaMenuColumns, setMegaMenuColumns] = useState([]);
  const location = useLocation();
  const { cartItems, setIsCartOpen } = useCart();
  const { logoUrl, siteName, logoHeight } = useSiteSettings();
  const { user, isAuthenticated, logout } = useAuth();
  const { openAuth } = useAuthDrawer();
  const { cart_enabled, pawn_checkout, events_enabled, activity_marketplace_enabled, service_crm_enabled, showcase_enabled, transfer_enabled, transfer_site_enabled } = useSiteFeatureFlags();
  // Phone comes from Settings -> Business Info, never a literal in the markup.
  const { phone } = useBusinessInfo();
  const phoneHref = telHref(phone);

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // "Free Tools" is driven by the admin-managed mega menu (Dev -> Mega Menu)
  // rather than a hardcoded list, so adding the next tool is a form entry,
  // not a code change. Root items are the columns; their children are the
  // links shown under each column header.
  useEffect(() => {
    let active = true;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/public/mega-menu/navigation`)
      .then((res) => (res.ok ? res.json() : { menu: [] }))
      .then((data) => {
        if (active) setMegaMenuColumns(Array.isArray(data.menu) ? data.menu : []);
      })
      .catch(() => {
        if (active) setMegaMenuColumns([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const resolveIcon = (name) => Icons[name] || LinkIcon;

  // Lock background scroll while the slide-out drawer is open.
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const forceDarkHeader = location.pathname === '/shop' || location.pathname.startsWith('/shop') || location.pathname === '/categories';

  const navigation = [
    {
      label: 'INDUSTRIES',
      items: [
        { label: 'HEALTHCARE', href: '/industries/healthcare' },
        { label: 'RETAIL', href: '/industries/retail-uses' },
        { label: 'WAREHOUSES', href: '/industries/warehouses' },
        { label: 'HOSPITALITY', href: '/industries/hospitality' },
        { label: 'EVENTS & STADIUMS', href: '/industries/events-stadiums' },
        { label: 'EDUCATION', href: '/industries/education' },
      ],
    },
    ...(cart_enabled ? [{ label: 'PARTS', href: '/shop/products?category=parts' }] : []),
    ...(events_enabled ? [{ label: 'EVENTS', href: '/events' }] : []),
    ...(transfer_enabled ? [{ label: 'SEND FILES', href: '/transfer' }] : []),
    ...(activity_marketplace_enabled ? [{
      label: 'ACTIVITIES',
      items: [
        { label: 'ALL ACTIVITIES', href: '/activities' },
        { label: 'BY CHARTER COMPANY', href: '/activities/companies' },
      ],
    }] : []),
    { label: 'SUPPORT', href: '/contact' },
    // Portfolio shares the showcase_enabled flag with the directory itself, so
    // switching the feature off removes the nav entry too rather than leaving
    // a link to a hidden page.
    ...(showcase_enabled === true ? [{ label: 'PORTFOLIO', href: '/apps' }] : []),
    {
      label: 'FREE TOOLS',
      href: '/tools',
      isMegaMenu: true,
    },
  ];

  const handleDropdownToggle = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  // Transfer Site template: the pages that still use this header (terms, privacy, login, ...) get the
  // transfer menu bar instead of the CRM navigation. They are dark pages, so the dark bar.
  if (transfer_site_enabled) return <TransferSiteHeader dark />;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || forceDarkHeader
            ? 'bg-bots-dark/95 backdrop-blur-md shadow-lg' 
            : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        {/* Top Bar - hidden on mobile to reduce clutter */}
        <div className="hidden md:block bg-bots-surface/80 border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center text-sm">
            {phoneHref ? (
              <a
                href={phoneHref}
                className="flex items-center text-white hover:text-blue-400 transition-colors"
                data-testid="header-phone"
              >
                <Phone className="w-4 h-4 mr-2" />
                {phone}
              </a>
            ) : (
              <span />
            )}
            <div className="flex items-center space-x-4">
              {cart_enabled && (
                <>
                  <Link
                    to="/shop"
                    className="text-gray-300 hover:text-white transition-colors"
                    data-testid="header-shop-link"
                  >
                    Shop
                  </Link>
                  <span className="text-gray-600">|</span>
                </>
              )}
              <Link 
                to="/123-bots-resources" 
                className="text-gray-300 hover:text-white transition-colors"
                data-testid="header-resources-link"
              >
                Resources
              </Link>
              <span className="text-gray-600">|</span>
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors"
                      data-testid="header-account-menu"
                    >
                      {user?.name || 'My Account'}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <div className="px-3 py-2 border-b">
                      <p className="font-medium text-sm truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="flex items-center gap-2 cursor-pointer" data-testid="header-my-account-link">
                        <User className="w-4 h-4" />
                        My Account
                      </Link>
                    </DropdownMenuItem>
                    {['admin', 'super_admin', 'staff'].includes(user?.role) && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                          <LayoutDashboard className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer" data-testid="header-logout-button">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to="/login"
                  onClick={(e) => { e.preventDefault(); openAuth('login'); }}
                  className="text-gray-300 hover:text-white transition-colors"
                  data-testid="header-login-link"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="header-logo">
              {logoUrl ? (
                // Height is a setting, not a Tailwind class, so a new logo's
                // proportions can be adjusted without a code change.
                <img
                  src={logoUrl}
                  alt={siteName || 'Home'}
                  style={{ height: `${logoHeight}px` }}
                  className="w-auto object-contain"
                />
              ) : (
                <span className="text-2xl font-bold text-white">{siteName}</span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <div key={item.label} className="relative group">
                  {(item.items || item.columns || item.isMegaMenu) ? (
                    <>
                      {item.href ? (
                        <Link
                          to={item.href}
                          className="flex items-center px-4 py-2 text-white hover:text-blue-400 transition-colors font-medium"
                          data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Link>
                      ) : (
                        <button
                          className="flex items-center px-4 py-2 text-white hover:text-blue-400 transition-colors font-medium"
                          onClick={() => handleDropdownToggle(item.label)}
                          data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </button>
                      )}
                      {/* Dropdown */}
                      {item.isMegaMenu ? (
                        megaMenuColumns.length > 0 && (
                          <div
                            className="absolute top-full left-1/2 -translate-x-1/2 w-[920px] max-w-[92vw] bg-bots-surface border border-gray-700 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 mt-1 p-6"
                            data-testid="nav-mega-menu-panel"
                          >
                            <div className="grid grid-cols-3 gap-8">
                              {megaMenuColumns.map((column) => {
                                const ColumnIcon = resolveIcon(column.icon);
                                return (
                                  <div key={column.id}>
                                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-700">
                                      <ColumnIcon className="w-4 h-4 text-[#FF6B00]" />
                                      <h3 className="text-[#FF6B00] font-bold text-sm whitespace-nowrap">
                                        {column.label}
                                      </h3>
                                    </div>
                                    <div className="space-y-1">
                                      {(column.children || []).map((child) => {
                                        const ChildIcon = resolveIcon(child.icon);
                                        const isExternal = child.open_in_new_tab || /^https?:\/\//.test(child.url);
                                        const content = (
                                          <>
                                            <div className="w-8 h-8 rounded-lg bg-[#4a3520]/40 flex items-center justify-center shrink-0 group-hover/item:bg-[#FF6B00]/20">
                                              <ChildIcon className="w-4 h-4 text-[#FF6B00]" />
                                            </div>
                                            <div>
                                              <p className="text-white text-sm font-semibold">{child.label}</p>
                                              {child.description && (
                                                <p className="text-gray-400 text-xs mt-0.5">{child.description}</p>
                                              )}
                                            </div>
                                          </>
                                        );
                                        return isExternal ? (
                                          <a
                                            key={child.id}
                                            href={child.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group/item flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-blue-500/10 transition-colors"
                                            data-testid={`nav-item-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                                          >
                                            {content}
                                          </a>
                                        ) : (
                                          <Link
                                            key={child.id}
                                            to={child.url}
                                            className="group/item flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-blue-500/10 transition-colors"
                                            data-testid={`nav-item-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                                          >
                                            {content}
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      ) : item.twoColumn ? (
                        <div className="absolute top-full left-0 w-[500px] bg-bots-surface border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 mt-1 p-4">
                          <div className="grid grid-cols-2 gap-6">
                            {item.columns.map((column, idx) => (
                              <div key={idx}>
                                <h3 className="text-blue-400 font-bold text-sm mb-3 pb-2 border-b border-gray-700 whitespace-nowrap">
                                  {column.title}
                                </h3>
                                {column.items.map((subItem) => (
                                  <Link
                                    key={subItem.label}
                                    to={subItem.href}
                                    className="block px-2 py-2 text-gray-300 hover:text-white hover:bg-blue-500/20 transition-colors rounded"
                                    data-testid={`nav-item-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    {subItem.label}
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="absolute top-full left-0 w-56 bg-bots-surface border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 mt-1">
                          {item.items.map((subItem) => (
                            subItem.external ? (
                              <a
                                key={subItem.label}
                                href={subItem.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-blue-500/20 transition-colors first:rounded-t-lg last:rounded-b-lg"
                                data-testid={`nav-item-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {subItem.label}
                              </a>
                            ) : (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-blue-500/20 transition-colors first:rounded-t-lg last:rounded-b-lg"
                                data-testid={`nav-item-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {subItem.label}
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="px-4 py-2 text-white hover:text-blue-400 transition-colors font-medium"
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Schedule Demo Button */}
              <Link
                to="/schedule-a-demo"
                className="hidden md:inline-flex px-6 py-2 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-500 transition-colors"
                data-testid="nav-schedule-demo"
              >
                SCHEDULE A DEMO
              </Link>

              {/* Buy or Lease Button - replaced by Schedule a Service when Service CRM is published */}
              {service_crm_enabled ? (
                <Link
                  to="/service-request"
                  className="hidden lg:inline-flex px-6 py-2 bg-green-500 text-black font-semibold rounded-full hover:bg-green-400 transition-colors"
                  data-testid="nav-schedule-service"
                >
                  SCHEDULE A SERVICE
                </Link>
              ) : (
                <Link
                  to="/schedule-a-demo"
                  className="hidden lg:inline-flex px-6 py-2 bg-[#FF6B00] text-black font-semibold rounded-full hover:bg-[#ff8124] transition-colors"
                  data-testid="nav-start-project"
                >
                  START A PROJECT
                </Link>
              )}

              {/* Cart Button */}
              {cart_enabled && pawn_checkout && (
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative p-2 text-white hover:text-blue-400 transition-colors"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </button>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

      </header>

      {/* Mobile Menu Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Menu - slides in from the right */}
      <div
        className={`lg:hidden fixed inset-y-0 right-0 z-50 w-[85vw] max-w-sm bg-bots-dark border-l border-gray-800 shadow-2xl overflow-y-auto transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu-panel"
      >
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
              <span className="text-white font-semibold">Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-white"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="px-4 py-4">
              {navigation.map((item) => (
                <div key={item.label} className="border-b border-gray-800 last:border-b-0">
                  {(item.items || item.columns || item.isMegaMenu) ? (
                    <>
                      <button
                        className="flex items-center justify-between w-full py-3 text-white font-medium"
                        onClick={() => handleDropdownToggle(item.label)}
                      >
                        {item.label}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            activeDropdown === item.label ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {activeDropdown === item.label && (
                        <div className="pb-3 pl-4 space-y-2">
                          {item.isMegaMenu ? (
                            megaMenuColumns.map((column) => {
                              const ColumnIcon = resolveIcon(column.icon);
                              return (
                                <div key={column.id} className="mb-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <ColumnIcon className="w-4 h-4 text-[#FF6B00]" />
                                    <h3 className="text-[#FF6B00] font-bold text-sm">{column.label}</h3>
                                  </div>
                                  {(column.children || []).map((child) => {
                                    const ChildIcon = resolveIcon(child.icon);
                                    const isExternal = child.open_in_new_tab || /^https?:\/\//.test(child.url);
                                    const content = (
                                      <>
                                        <ChildIcon className="w-4 h-4 text-[#888680] shrink-0" />
                                        <span>
                                          {child.label}
                                          {child.description && (
                                            <span className="block text-xs text-gray-500">{child.description}</span>
                                          )}
                                        </span>
                                      </>
                                    );
                                    return isExternal ? (
                                      <a
                                        key={child.id}
                                        href={child.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 py-2 text-gray-400 hover:text-white transition-colors"
                                      >
                                        {content}
                                      </a>
                                    ) : (
                                      <Link
                                        key={child.id}
                                        to={child.url}
                                        className="flex items-center gap-2 py-2 text-gray-400 hover:text-white transition-colors"
                                      >
                                        {content}
                                      </Link>
                                    );
                                  })}
                                </div>
                              );
                            })
                          ) : item.twoColumn ? (
                            item.columns.map((column, idx) => (
                              <div key={idx} className="mb-4">
                                <h3 className="text-blue-400 font-bold text-sm mb-2">
                                  {column.title}
                                </h3>
                                {column.items.map((subItem) => (
                                  <Link
                                    key={subItem.label}
                                    to={subItem.href}
                                    className="block py-2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {subItem.label}
                                  </Link>
                                ))}
                              </div>
                            ))
                          ) : (
                            item.items.map((subItem) => (
                              subItem.external ? (
                                <a
                                  key={subItem.label}
                                  href={subItem.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                  {subItem.label}
                                </a>
                              ) : (
                                <Link
                                  key={subItem.label}
                                  to={subItem.href}
                                  className="block py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                  {subItem.label}
                                </Link>
                              )
                            ))
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-3 text-white font-medium"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="mt-4 space-y-3">
                <Link
                  to="/schedule-a-demo"
                  className="block w-full py-3 bg-blue-600 text-white font-semibold rounded-full text-center hover:bg-blue-500 transition-colors"
                >
                  SCHEDULE A DEMO
                </Link>
                {service_crm_enabled ? (
                  <Link
                    to="/service-request"
                    className="block w-full py-3 bg-green-500 text-black font-semibold rounded-full text-center hover:bg-green-400 transition-colors"
                  >
                    SCHEDULE A SERVICE
                  </Link>
                ) : (
                  <Link
                    to="/schedule-a-demo"
                    className="block w-full py-3 bg-[#FF6B00] text-black font-semibold rounded-full text-center hover:bg-[#ff8124] transition-colors"
                    data-testid="mobile-start-project"
                  >
                    START A PROJECT
                  </Link>
                )}
              </div>

              {/* Mobile secondary links (moved from hidden top bar) */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-gray-800 pt-4 text-sm">
                {cart_enabled && (
                  <Link to="/shop" className="text-gray-300 hover:text-white" data-testid="mobile-shop-link">Shop</Link>
                )}
                <Link to="/123-bots-resources" className="text-gray-300 hover:text-white" data-testid="mobile-resources-link">Resources</Link>
                {isAuthenticated ? (
                  <>
                    <Link to="/account" className="text-gray-300 hover:text-white" data-testid="mobile-my-account-link">My Account</Link>
                    <button onClick={logout} className="text-gray-300 hover:text-white" data-testid="mobile-logout-button">Logout</button>
                  </>
                ) : (
                  <Link to="/login" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); openAuth('login'); }} className="text-gray-300 hover:text-white" data-testid="mobile-login-link">Login</Link>
                )}
                {phoneHref && (
                  <a href={phoneHref} className="flex items-center gap-1 text-gray-300 hover:text-white" data-testid="mobile-phone-link"><Phone className="w-3.5 h-3.5" /> {phone}</a>
                )}
              </div>
            </div>
      </div>

      {/* Cart Drawer */}
      <CartDrawer />
    </>
  );
};

export default Header;
